// Палітра пошуку Ctrl+K / ⌘K (SPEC D37, ТЗ §34): одне поле в топбарі; групи, викладачі, дисципліни, аудиторії, корпуси, кафедри,
// факультети, студенти (за правами); часові вирази («Іваненко наступного тижня») → розклад об'єкта на діапазон. Клавіатура:
// ↑/↓ — вибір, Enter — перейти, Esc — закрити. Останні запити — у localStorage (лише зручність цього браузера).
import { SEARCH_KIND_LABELS, searchRangeText, type SearchItem, type SearchKind } from '@unisched/shared';
import {
  BookOpen,
  Building2,
  CalendarRange,
  DoorOpen,
  GraduationCap,
  History,
  Landmark,
  Network,
  Search,
  UserRound,
  UsersRound,
  type LucideIcon,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router';
import { cn } from '@/lib/cn';
import { useDebouncedValue, useLocalStorageState } from '@/lib/hooks';
import { Dialog, EmptyState, Input, Kbd, Spinner } from '@/ui';
import { useSearch } from '../api';

const KIND_ICONS: Record<SearchKind, LucideIcon> = {
  GROUP: UsersRound,
  TEACHER: UserRound,
  SUBJECT: BookOpen,
  ROOM: DoorOpen,
  LOCATION: Building2,
  DEPARTMENT: Network,
  FACULTY: Landmark,
  STUDENT: GraduationCap,
};

const RECENT_KEY = 'unisched.search.recent';
const RECENT_MAX = 6;

function SearchResults({
  items,
  active,
  onHover,
  onPick,
}: {
  items: readonly SearchItem[];
  active: number;
  onHover: (i: number) => void;
  onPick: (item: SearchItem) => void;
}) {
  return (
    <ul role="listbox" id="search-results" aria-label="Результати пошуку" className="flex max-h-[50vh] flex-col gap-0.5 overflow-y-auto">
      {items.map((it, i) => {
        const Icon = KIND_ICONS[it.kind];
        return (
          <li
            key={`${it.kind}:${it.id}`}
            id={`search-opt-${i}`}
            role="option"
            aria-selected={i === active}
            className={cn('flex cursor-pointer items-center gap-3 rounded-field px-2.5 py-2', i === active ? 'bg-primary-soft text-primary-ink' : 'hover:bg-row-hover')}
            onMouseEnter={() => onHover(i)}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onPick(it)}
          >
            <Icon className="size-4 shrink-0 text-muted" aria-hidden />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[14px] font-semibold">{it.label}</span>
              <span className="block truncate text-[12px] text-muted">
                {SEARCH_KIND_LABELS[it.kind]}
                {it.sublabel ? ` · ${it.sublabel}` : ''}
                {it.code && it.code !== it.label ? ` · ${it.code}` : ''}
              </span>
            </span>
          </li>
        );
      })}
    </ul>
  );
}

export function SearchDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const [recent, setRecent] = useLocalStorageState<string[]>(RECENT_KEY, []);
  const debounced = useDebouncedValue(q, 200);
  const res = useSearch(open ? debounced : '');
  const items = useMemo(() => (debounced.trim() ? (res.data?.items ?? []) : []), [debounced, res.data]);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) setQ('');
  }, [open]);
  useEffect(() => setActive(0), [debounced]);

  const remember = (text: string) => {
    const t = text.trim();
    if (!t) return;
    setRecent([t, ...(Array.isArray(recent) ? recent : []).filter((x) => x !== t)].slice(0, RECENT_MAX));
  };

  const pick = (item: SearchItem) => {
    remember(q);
    onClose();
    navigate(item.link);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => (items.length ? (a + 1) % items.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => (items.length ? (a - 1 + items.length) % items.length : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const it = items[active];
      if (it) pick(it);
    }
  };

  const range = res.data?.range ?? null;
  const searching = res.isFetching && debounced.trim().length > 0;
  return (
    <Dialog open={open} onClose={onClose} title="Пошук" size="lg" description="Групи, викладачі, дисципліни, аудиторії, корпуси, кафедри, факультети.">
      <div className="flex flex-col gap-3">
        <Input
          ref={inputRef}
          data-autofocus
          icon={Search}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Наприклад: «Іваненко наступного тижня», «101 у п’ятницю» або код групи"
          aria-label="Пошуковий запит"
          role="combobox"
          aria-expanded={items.length > 0}
          aria-controls="search-results"
          aria-activedescendant={items.length ? `search-opt-${active}` : undefined}
          suffix={searching ? <Spinner size="xs" /> : undefined}
        />
        {range ? (
          <p className="flex items-center gap-2 text-[13px] text-primary-ink">
            <CalendarRange className="size-4" aria-hidden />
            {searchRangeText(range)}
          </p>
        ) : null}
        {!q.trim() ? (
          Array.isArray(recent) && recent.length ? (
            <div className="flex flex-col gap-1">
              <p className="text-[12px] font-semibold text-muted">Останні запити</p>
              <ul className="flex flex-wrap gap-1.5">
                {recent.map((r) => (
                  <li key={r}>
                    <button
                      type="button"
                      className="flex items-center gap-1.5 rounded-chip border border-border px-2.5 py-1 text-[13px] hover:bg-row-hover"
                      onClick={() => {
                        setQ(r);
                        inputRef.current?.focus();
                      }}
                    >
                      <History className="size-3.5 text-muted" aria-hidden />
                      {r}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-[13px] text-muted">Введіть початок назви, прізвища або код. Можна додати час: «завтра», «наступного тижня», «з 01.10 по 05.10».</p>
          )
        ) : items.length ? (
          <SearchResults items={items} active={active} onHover={setActive} onPick={pick} />
        ) : res.isFetching || debounced !== q ? null : (
          <EmptyState compact icon={Search} title="Нічого не знайдено" description="Введіть початок прізвища, назви або код (без закінчень)." />
        )}
        <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-muted">
          <span className="flex items-center gap-1">
            <Kbd>↑</Kbd>
            <Kbd>↓</Kbd> вибір
          </span>
          <span className="flex items-center gap-1">
            <Kbd>Enter</Kbd> перейти
          </span>
          <span className="flex items-center gap-1">
            <Kbd>Esc</Kbd> закрити
          </span>
        </p>
      </div>
    </Dialog>
  );
}

/** Кнопка пошуку в топбарі + Ctrl+K / ⌘K + палітра. */
export function SearchPalette() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.altKey && (e.code === 'KeyK' || e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex h-9 items-center gap-2 rounded-field border border-border bg-surface-2 px-2.5 text-[13px] text-muted transition-colors hover:border-border-strong hover:text-fg sm:w-64 sm:px-3"
        aria-label="Пошук (Ctrl+K)"
        aria-keyshortcuts="Control+K Meta+K"
      >
        <Search className="size-4 shrink-0" aria-hidden />
        <span className="hidden flex-1 text-left sm:inline">Пошук…</span>
        <span className="hidden items-center gap-0.5 sm:flex">
          <Kbd>Ctrl</Kbd>
          <Kbd>K</Kbd>
        </span>
      </button>
      <SearchDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}
